import { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, useJoyrideOptions } from './tourUtils';

interface ProjectPlayerTourProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

/** Delay (ms) after `open` flips true before resolving anchors — the player
 * mounts asynchronously (episode auto-select renders the waveform/editor). */
const ANCHOR_MOUNT_DELAY = 300;

export const ProjectPlayerTour = ({ open, onComplete, onSkip }: ProjectPlayerTourProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      // Desktop: 4 steps
      const desktopSteps: Step[] = [
        {
          target: '[data-tour="episode-list"]',
          title: t('playerTour.step1.title', 'Select an Episode'),
          content: t(
            'playerTour.step1.description',
            'Choose a podcast episode from your project to start learning. The audio player will load automatically.'
          ),
        },
        {
          target: '[data-tour="add-segment"]',
          spotlightTarget: '[data-tour="waveform"]',
          title: t('playerTour.step2.title', 'Create a Segment'),
          content: t(
            'playerTour.step2.description',
            'Click "Add Segment" to create a region on the waveform. Drag the handles to select the audio section you want to study.'
          ),
        },
        {
          target: '[data-tour="segment-editor"]',
          placement: 'top',
          title: t('playerTour.step3.title', 'Add Transcription & Translation'),
          content: t(
            'playerTour.step3.description',
            'Expand the segment to add transcriptions and translations. Use auto-generate or type manually. Select words to add to vocabulary!'
          ),
        },
        {
          target: '[data-tour="loop-button"]',
          placement: 'top',
          title: t('playerTour.step4.title', 'Loop & Learn'),
          content: t(
            'playerTour.step4.description',
            'Click the loop button to repeat your segment. Listen while reading the transcript and translation to reinforce learning.'
          ),
        },
      ];

      // Mobile: 3 steps (merge transcribe/translate with loop)
      const mobileSteps: Step[] = [
        {
          target: '[data-tour="episode-list"]',
          title: t('playerTour.step1.title', 'Select an Episode'),
          content: t(
            'playerTour.step1.description',
            'Choose a podcast episode from your project to start learning. The audio player will load automatically.'
          ),
        },
        {
          // Tablet widths (sm..md) still count as "mobile" for step count but
          // render the desktop button, so accept whichever twin is visible.
          target: '[data-tour="add-segment-mobile"], [data-tour="add-segment"]',
          title: t('playerTour.step2.title', 'Create a Segment'),
          content: t(
            'playerTour.step2.description',
            'Click "Add Segment" to create a region on the waveform. Drag the handles to select the audio section you want to study.'
          ),
        },
        {
          target: '[data-tour="segment-editor"]',
          placement: 'top',
          title: t('playerTour.stepMobile3.title', 'Study Your Segments'),
          content: t(
            'playerTour.stepMobile3.description',
            'Add transcriptions and translations, then loop segments to practice. Select words to build your vocabulary!'
          ),
        },
      ];

      setSteps(filterValidSteps(isMobile ? mobileSteps : desktopSteps));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
  }, [open, isMobile, t]);

  const handleEvent: EventHandler = (data) => {
    if (data.type !== EVENTS.TOUR_END) {
      return;
    }
    if (data.status === STATUS.FINISHED) {
      // Persists player_tour_completed via useProjectPlayerTour
      onComplete();
    } else if (data.status === STATUS.SKIPPED) {
      // Persists player_tour_skipped (badge shows on the HelpOutline button)
      onSkip();
    }
  };

  // If no anchors survived filtering, render nothing without completing —
  // the tour retries on next mount when the anchors exist.
  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      continuous
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        blockTargetInteraction: true,
        closeButtonAction: 'skip',
        overlayClickAction: false,
      }}
    />
  );
};
